import {
  DetalleDeBolsaDeMinerosSchema,
  ListaDeBolsasDeMinerosSchema,
  ResultadoDeCargaDeMinerosSchema,
  ResultadoDeDeclaracionesDeMinerosSchema,
  ResumenDeMinerosSchema,
} from '@cnc/contracts';
import {keepPreviousData, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {pedir} from './cliente';
import {refrescarDatos} from './refrescar';

// Inventario de Mineros (decision 0022), con la fecha de corte y los filtros de la direccion.
export function useResumenDeMineros(corte: string | null, tenor: string | null = null) {
  const cadena = new URLSearchParams({...(corte ? {corte} : {}), ...(tenor ? {tenor} : {})}).toString();
  return useQuery({
    queryKey: ['mineros', 'resumen', cadena],
    queryFn: () => pedir(`mineros/resumen${cadena ? `?${cadena}` : ''}`, ResumenDeMinerosSchema),
    placeholderData: keepPreviousData,
  });
}

export function useBolsasDeMineros(parametros: URLSearchParams) {
  const cadena = parametros.toString();
  return useQuery({
    queryKey: ['mineros', 'bolsas', cadena],
    queryFn: () => pedir(`mineros/bolsas${cadena ? `?${cadena}` : ''}`, ListaDeBolsasDeMinerosSchema),
    placeholderData: keepPreviousData,
  });
}

export const rutaExcelDeMineros = (parametros: URLSearchParams) => {
  const cadena = parametros.toString();
  return `/api/mineros/bolsas/excel${cadena ? `?${cadena}` : ''}`;
};

// Informe de inventario de concentrados (decision 0089): viajes de lunes a viernes.
export const rutaInformeDeMineros = (corte: string, vuelos: number[], tractomulas: number[]) =>
  `/api/mineros/informe?${new URLSearchParams({corte, vuelos: vuelos.join(','), tractomulas: tractomulas.join(',')})}`;

export function useDetalleDeMineros(bbNo: string, activo = true) {
  return useQuery({
    queryKey: ['mineros', 'detalle', bbNo],
    queryFn: () => pedir(`mineros/bolsas/${encodeURIComponent(bbNo)}`, DetalleDeBolsaDeMinerosSchema),
    enabled: activo,
    retry: false,
  });
}

export function useCargarMineros() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({
      envios,
      bodega,
      listas,
      declaraciones,
      modo,
    }: {
      envios: File;
      // Sin base de la bodega cuando se carga el DB Mineros Consolidated (la trae).
      bodega: File | null;
      listas: File[];
      declaraciones: File[];
      modo: 'revisar' | 'aplicar';
    }) => {
      const cuerpo = new FormData();
      cuerpo.append('envios', envios, envios.name);
      if (bodega) cuerpo.append('bodega', bodega, bodega.name);
      for (const l of listas) cuerpo.append('listas', l, l.name);
      for (const d of declaraciones) cuerpo.append('declaraciones', d, d.name);
      cuerpo.append('modo', modo);
      return pedir('mineros/cargar', ResultadoDeCargaDeMinerosSchema, {method: 'POST', body: cuerpo});
    },
    onSuccess: async r => {
      if (r.aplicado) await refrescarDatos(cliente);
    },
  });
}

// Solo las declaraciones de valor (TM): sus Big Bags pasan de la planta a la bodega.
export function useCargarDeclaracionesDeMineros() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({declaraciones, modo}: {declaraciones: File[]; modo: 'revisar' | 'aplicar'}) => {
      const cuerpo = new FormData();
      for (const d of declaraciones) cuerpo.append('declaraciones', d, d.name);
      cuerpo.append('modo', modo);
      return pedir('mineros/declaraciones', ResultadoDeDeclaracionesDeMinerosSchema, {method: 'POST', body: cuerpo});
    },
    onSuccess: async r => {
      if (r.aplicado) await refrescarDatos(cliente);
    },
  });
}
